import { useEffect, useRef, useCallback, useContext } from 'react';
import { WebSocketContext } from '../providers/websocket-context/websocket-provider';

type WakeCallback = () => void;

export const useSmartInactivityWake = (
  onWake: WakeCallback,
  thresholdMs: number = 5 * 60 * 1000,
) => {
  const { isConnected } = useContext(WebSocketContext);
  const lastHiddenAtRef = useRef<number>(Date.now());
  const lastWakeAtRef = useRef<number>(0);

  const checkWake = useCallback(() => {
    if (!isConnected) return;

    const now = Date.now();
    const sinceHidden = now - lastHiddenAtRef.current;
    const sinceWake = now - lastWakeAtRef.current;

    if (sinceHidden >= thresholdMs && sinceWake >= thresholdMs) {
      lastWakeAtRef.current = now;
      onWake();
    }
  }, [isConnected, onWake, thresholdMs]);

  useEffect(() => {
    const handleHidden = () => {
      lastHiddenAtRef.current = Date.now();
    };
    const handleWake = () => {
      checkWake();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        handleHidden();
      } else {
        handleWake();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    window.addEventListener('blur', handleHidden);
    window.addEventListener('focus', handleWake);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', handleHidden);
      window.removeEventListener('focus', handleWake);
    };
  }, [checkWake]);
};
